import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import Clickable from 'components/Common/Clickable'

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05);
`

const CardHeader = styled.div`
  padding: ${rem(10)} ${rem(12)};
  ${tierGradientBackgroundStyle}

  /* Platinum uses a custom gradient instead of gradientPrimaryBackground */
  &.tier-platinum {
    background: linear-gradient(
      45deg,
      ${props => props.theme.palette.product.loyalty.platinum.darkForeground} 0.73%,
      ${props => props.theme.palette.product.loyalty.platinum.lightBackground} 45.77%,
      ${props => props.theme.palette.product.loyalty.platinum.darkForeground} 74.49%
    );
  }
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(14)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(18)};
  ${tierNormalContrastColorStyle}

  /* Platinum uses darkContrast instead of normalContrast */
  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)};
`

const HeaderToggle = styled.div`
  display: inline-flex;
  align-items: stretch;
  background: rgba(0, 0, 0, 0.15);
  border-radius: ${rem(5)};
  padding: ${rem(2)};
  gap: ${rem(2)};
`

const HeaderToggleOption = styled(Clickable)`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${rem(3)};
  transition: background 150ms, opacity 150ms;
  ${tierNormalContrastColorStyle}

  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }

  &.is-active {
    font-weight: ${props => props.theme.font.primary.weight.bold};
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }

  &.is-inactive {
    font-weight: ${props => props.theme.font.primary.weight.regular};
    opacity: 0.7;
  }
`

const BodyTextStyled = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  /* Using bronze contrast instead of neural one to get the same color as the figma design */
  color: ${props => props.theme.palette.neutral.default.one};
  margin: 0;
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.superBold};
`

interface Props {
  /**
   * The tier to display (next tier for unlock, current tier for retain)
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  creditsRemaining: number
  nightsRemaining: number
  expiryDate?: string
  showToggle?: boolean
  showUpgradeView?: boolean
  onToggleView?: (showUpgrade: boolean) => void
  currentTierName?: string
  nextTierName?: string
}

function LoyaltyDrawerTierCard({
  tier,
  isRetain = false,
  creditsRemaining,
  nightsRemaining,
  expiryDate,
  showToggle = false,
  showUpgradeView = false,
  onToggleView,
  currentTierName,
  nextTierName,
}: Props) {
  const intl = useIntl()
  const tierName = getLoyaltyTierDisplayName(tier)

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  const toRetainText = intl.formatMessage(
    {
      id: 'zvrL6f2WsFsW2a8dOo7t',
      defaultMessage: 'To retain {tierName}',
      description: 'Header text for tier retention requirement in the loyalty section',
    },
    { tierName },
  )
  const toUnlockText = intl.formatMessage(
    {
      id: 'LPYDBrvhK439rFvswhrv',
      defaultMessage: 'To unlock {tierName}',
      description: 'Header text for tier unlock requirement in the loyalty program section',
    },
    { tierName },
  )
  const headerText = isRetain ? toRetainText : toUnlockText

  const formattedExpiryDate = expiryDate ? <FormatDateTime value={expiryDate} format="dmy-casual-short" /> : undefined
  const boldTag = (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>

  let bodyText: React.ReactNode
  if (hasCredits && hasNights && expiryDate) {
    bodyText = <FormattedMessage
      id="HbnvaQmTGprfYgxmGYnS"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# night} other {# nights}}</bold> by {expiryDate}."
      description="Drawer card: credits and nights needed with expiry date"
      values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else if (hasCredits && hasNights) {
    bodyText = <FormattedMessage
      id="BksWpQ-lkV8lLNCvBXJI"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> and <bold>{nightsCount, plural, one {# night} other {# nights}}</bold>."
      description="Drawer card: credits and nights needed, no expiry in the loyalty program section"
      values={{ creditsCount: creditsRemaining, nightsCount: nightsRemaining, bold: boldTag }}
    />
  } else if (hasCredits && expiryDate) {
    bodyText = <FormattedMessage
      id="-F7esi0lfIcxuwcZqIKj"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold> by {expiryDate}."
      description="Drawer card: credits needed with expiry date in the loyalty program section"
      values={{ creditsCount: creditsRemaining, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else if (hasCredits) {
    bodyText = <FormattedMessage
      id="GVNhIi94GzO8temKvHWX"
      defaultMessage="You need <bold>{creditsCount, plural, one {# Status Credit} other {# Status Credits}}</bold>."
      description="Drawer card: credits needed, no expiry in the loyalty program section"
      values={{ creditsCount: creditsRemaining, bold: boldTag }}
    />
  } else if (expiryDate) {
    bodyText = <FormattedMessage
      id="BRIAaL7ikGNRklDOVldH"
      defaultMessage="You need <bold>{nightsCount, plural, one {# night} other {# nights}}</bold> by {expiryDate}."
      description="Drawer card: nights needed with expiry date in the loyalty program section"
      values={{ nightsCount: nightsRemaining, expiryDate: formattedExpiryDate, bold: boldTag }}
    />
  } else {
    bodyText = <FormattedMessage
      id="iAlsAllYSdzDk9L5woEp"
      defaultMessage="You need <bold>{nightsCount, plural, one {# night} other {# nights}}</bold>."
      description="Drawer card: nights needed, no expiry in the loyalty program section"
      values={{ nightsCount: nightsRemaining, bold: boldTag }}
    />
  }

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        {showToggle && currentTierName && nextTierName && (
          <HeaderToggle>
            <HeaderToggleOption
              className={`tier-${tier} ${!showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onToggleView?.(false) }}
            >
              {intl.formatMessage(
                { id: '9ZN5FHO5pQctKoGZ26Aw', defaultMessage: 'Retain {tierName}', description: 'Toggle option to view current tier retention requirements' },
                { tierName: currentTierName },
              )}
            </HeaderToggleOption>
            <HeaderToggleOption
              className={`tier-${tier} ${showUpgradeView ? 'is-active' : 'is-inactive'}`}
              onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onToggleView?.(true) }}
            >
              {intl.formatMessage(
                { id: 'NilqMdlNHrDTNigNu1IR', defaultMessage: 'Unlock {tierName}', description: 'Toggle option to view next tier upgrade requirements' },
                { tierName: nextTierName },
              )}
            </HeaderToggleOption>
          </HeaderToggle>
        )}
        {!(showToggle && currentTierName && nextTierName) && (
          <HeaderText className={`tier-${tier}`}>
            {headerText}
          </HeaderText>
        )}
      </CardHeader>
      <CardBody>
        <BodyTextStyled>
          {bodyText}
        </BodyTextStyled>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyDrawerTierCard
